import { cn, TooltipWrapper } from '@wellesley/ui'
import { type VariantProps } from 'class-variance-authority'
import { type FC, type PropsWithChildren } from 'react'
import {
  Info,
  BadgeInfo,
  OctagonAlert,
  TriangleAlert,
  CircleCheck,
  type LucideIcon,
} from 'lucide-react'

import variants from './variants'

const TYPE_ICON: Record<string, LucideIcon> = {
  neutral: BadgeInfo,
  error: TriangleAlert,
  warning: OctagonAlert,
  success: CircleCheck,
}

type AlertProps = PropsWithChildren &
  VariantProps<typeof variants> & {
    icon?: React.ReactNode
    title?: string
    className?: string
    dataTestId?: string
    tooltipContent?: string
  }

const Alert: FC<AlertProps> = ({
  type = 'neutral',
  style = 'filled',
  title,
  icon,
  className,
  children,
  tooltipContent,
  dataTestId = `alert-${type}-${style}`,
}) => {
  const IconComponent = TYPE_ICON[type ?? 'neutral']

  return (
    <div
      role="alert"
      data-testid={dataTestId}
      className={cn(variants({ type, style }), className)}
    >
      {icon ?? <IconComponent size={20} className="text-current" aria-hidden />}
      <div className="gap-xxxs flex min-w-0 flex-1 flex-col">
        {title && (
          <p className="text-base font-semibold text-current">{title}</p>
        )}
        {children && <p className="text-base text-gray-700">{children}</p>}
      </div>
      {tooltipContent && (
        <TooltipWrapper
          tooltipText={tooltipContent}
          size="small"
          theme="dark"
          side="top"
          sideOffset={40}
        >
          <Info size={20} color="var(--gray-500)" />
        </TooltipWrapper>
      )}
    </div>
  )
}

export default Alert
export type { AlertProps }
